/**
 * Soft≠Live Page165 — SA Platform Models residual liquid (page82/122 + status).
 * Mount: /sa/models
 * Buses:
 *   GET /sa/models/status (always-on Live posture)
 *   GET/PUT/DELETE /ai/models/platform*
 *   GET/PUT /ai/models/byok-policy/platform
 *   GET/POST/DELETE …/platform/{id}/grants
 * Soft residual deleted:
 *  - sandpit list without liquid rail / KPI / Re-read / status SoT
 *  - bare Publish / Remove / Grants / Grant user / BYOK CTAs without
 *  - Soft invent empty catalogue when deployment not armed
 *  - Soft merge of Settings→Model access into this staff desk
 */
import { useMemo, useState } from "react";
import { html, F, Ico, Input, Field, Card, Alert, Ticker, go } from "./ui.js";
import { path, useApi } from "./api.js";
import {
  kindOf,
  useByok,
  useCatalogue,
  useGrants,
  useModelWrites,
} from "./models-api.js";

/** Pure: KPI strip from Live platform catalogue only — never invent Soft counts. */
export function platformKpisOf(models, byokPolicy) {
  const list = Array.isArray(models) ? models : [];
  const local = list.filter((m) => kindOf(m) === "local").length;
  const hosted = list.length - local;
  const withSecret = list.filter((m) => m && (m.has_secret || m.provider === "ollama")).length;
  const defaults = list.filter((m) => m && m.is_default).length;
  let byok = null;
  if (byokPolicy && typeof byokPolicy.allow_byok === "boolean") {
    byok = byokPolicy.allow_byok ? 1 : 0;
  }
  return {
    published: list.length,
    local,
    hosted,
    withSecret,
    defaults,
    byok,
  };
}

export function SaModelsDesk({ toast }) {
  const posture = useApi(path("get__sa_models_status"));
  // Fail-closed while loading — no Soft catalogue probe invent.
  const post = posture.loading || !posture.data ? {} : (posture.data || {});
  const product = post.product_flag === true
    ? true
    : (post.product_flag === false ? false : null);
  const seat = post.list_allowed === true;
  const writeArmed = post.write_allowed === true;
  const detail = post.detail || null;

  const catalogueWhich = seat ? "platform" : null;
  const byokScope = seat ? "platform" : null;
  const writeScope = writeArmed ? "platform" : null;

  const cat = useCatalogue(catalogueWhich);
  const byok = useByok(byokScope);
  const writes = useModelWrites(writeScope || "platform");
  const [sel, setSel] = useState("");
  const [draft, setDraft] = useState({
    label: "",
    provider: "openai_compat",
    base_url: "",
    text_model: "",
    api_key: "",
  });
  const [grantUser, setGrantUser] = useState("");
  const grants = useGrants(writeArmed ? "platform" : null, (writeArmed && sel) || null);
  const kpis = useMemo(
    () => platformKpisOf(cat.models, byok.policy),
    [cat.models, byok.policy],
  );

  const reRead = () => {
    posture.reload && posture.reload();
    cat.reload && cat.reload();
    byok.reload && byok.reload();
    grants.reload && grants.reload();
    toast && toast("Platform models posture + catalogue re-read", "go");
  };

  // Status SoT first: unarmed flag, then seat, then catalogue residual honesty.
  if (!posture.loading && post.product_flag === false) {
    return html`<div className="sa-models-desk sa-models-desk--liquid"
      data-cmc-page="page122-sa-models" data-cmc-sa-models="page82"
      data-cmc-residual="page165-sa-models">
      <div className="sa-models-desk__rail">
        <div>
          <p className="eyebrow">Platform · Models</p>
          <h2 className="sa-models-desk__title">Catalogue not armed</h2>
          <p className="note">
            Platform catalogue stays empty until AI platform is armed on deployment.
          </p>
        </div>
        <div className="btn-row sa-models-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
            disabled=${posture.loading}>
            <${Ico} d="refresh" w=${14}/> Re-read posture
          </button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/settings/models")}>Settings · Model access</button>
        </div>
      </div>
      <${Card} title="SA models posture" meta="Live">
        <p className="note">
          Product off · ${detail || "AI platform disabled on this deployment"}
        </p>
      <//>
      <${Alert} tone="warn" title="Model catalogue is not armed"
        say="Platform models stay empty until staff arm AI platform on this deployment."/>
    </div>`;
  }

  if (!posture.loading && post.list_allowed === false && post.product_flag !== false) {
    return html`<div className="sa-models-desk sa-models-desk--liquid"
      data-cmc-page="page122-sa-models" data-cmc-sa-models="page82"
      data-cmc-residual="page165-sa-models">
      <div className="sa-models-desk__rail">
        <div>
          <p className="eyebrow">Platform · Models</p>
          <h2 className="sa-models-desk__title">Seat gated</h2>
          <p className="note">Publishing platform models needs superadmin / sa_ai_admin. Soft merge into Settings is forbidden.</p>
        </div>
        <div className="btn-row sa-models-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
            disabled=${posture.loading}>
            <${Ico} d="refresh" w=${14}/> Re-read posture
          </button>
          <button type="button" className="btn btn--go btn--sm"
            onClick=${() => go("/settings/models")}>Open Settings · Model access</button>
        </div>
      </div>
      <${Card} title="SA models posture" meta="Live">
        <p className="note">
          list_allowed no · write_allowed no
          · detail ${detail || "sa_models_seat_gated"}
          · product ${post.product_flag ? "on" : "off"}
        </p>
      <//>
      <${Alert} tone="info" title="Not yours to administer"
        say="Tenant admins use Settings → Model access for their own account catalogue. Platform publish needs a write seat."/>
    </div>`;
  }

  // Catalogue residual while posture still loading or status open.
  if (catalogueWhich && !cat.armed) {
    return html`<div className="sa-models-desk sa-models-desk--liquid"
      data-cmc-page="page122-sa-models" data-cmc-sa-models="page82"
      data-cmc-residual="page165-sa-models">
      <div className="sa-models-desk__rail">
        <div>
          <p className="eyebrow">Platform · Models</p>
          <h2 className="sa-models-desk__title">Catalogue not armed</h2>
          <p className="note">
            Catalogue loads only after product and list seats open.
          </p>
        </div>
        <div className="btn-row sa-models-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
            disabled=${cat.loading || posture.loading}>
            <${Ico} d="refresh" w=${14}/> Re-read
          </button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/settings/models")}>Settings · Model access</button>
        </div>
      </div>
      <${Alert} tone="warn" title="Model catalogue is not armed"
        say="Platform models stay empty until staff arm the catalogue on this deployment. Empty catalogues are not invented."/>
    </div>`;
  }

  if (catalogueWhich && cat.forbidden) {
    return html`<div className="sa-models-desk sa-models-desk--liquid"
      data-cmc-page="page122-sa-models" data-cmc-sa-models="page82"
      data-cmc-residual="page165-sa-models">
      <div className="sa-models-desk__rail">
        <div>
          <p className="eyebrow">Platform · Models</p>
          <h2 className="sa-models-desk__title">Seat gated</h2>
          <p className="note">Platform catalogue refused for this account.</p>
        </div>
        <div className="btn-row sa-models-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
            disabled=${cat.loading || posture.loading}>
            <${Ico} d="refresh" w=${14}/> Re-read
          </button>
          <button type="button" className="btn btn--go btn--sm"
            onClick=${() => go("/settings/models")}>Open Settings · Model access</button>
        </div>
      </div>
      <${Alert} tone="info" title="Not yours to administer"
        say="Tenant admins use Settings → Model access. Platform publish needs a write seat."/>
    </div>`;
  }

  const publish = async () => {
    if (!writeArmed) {
      toast && toast("Publish is closed on this seat.", "warn");
      return;
    }
    const r = await writes.save({
      label: draft.label || "Platform model",
      provider: draft.provider,
      base_url: draft.base_url,
      text_model: draft.text_model,
      api_key: draft.api_key || undefined,
      is_default: false,
    });
    if (r) {
      toast && toast("Platform model published", "go");
      setDraft({ label: "", provider: "openai_compat", base_url: "", text_model: "", api_key: "" });
      cat.reload();
    } else toast && toast(writes.error || "Publish refused · nothing was changed", "bad");
  };

  const flipByok = async () => {
    if (!writeArmed) {
      toast && toast("BYOK policy write is closed on this seat.", "warn");
      return;
    }
    const next = !(byok.policy && byok.policy.allow_byok);
    const r = await writes.setByok(next);
    if (r) {
      byok.reload();
      toast && toast(
        next ? "Platform BYOK ceiling on" : "Platform BYOK ceiling off",
        "go",
      );
    } else toast && toast(writes.error || "BYOK change refused · nothing was changed", "bad");
  };

  const grant = async () => {
    if (!writeArmed) {
      toast && toast("Grant is closed on this seat.", "warn");
      return;
    }
    if (!sel || !grantUser.trim()) return;
    const r = await writes.grant(sel, "user", grantUser.trim());
    if (r) {
      toast && toast("Granted · " + grantUser.trim(), "go");
      setGrantUser("");
      grants.reload();
    } else toast && toast(writes.error || "Grant refused · nothing was changed", "bad");
  };

  return html`<div className="sa-models-desk sa-models-desk--liquid"
    data-cmc-page="page122-sa-models" data-cmc-sa-models="page82"
    data-cmc-residual="page165-sa-models">
    <div className="sa-models-desk__rail">
      <div>
        <p className="eyebrow">Platform · Models</p>
        <h2 className="sa-models-desk__title">Staff catalogue</h2>
        <p className="note">
          Tenant / mine catalogues stay under Settings → Model access.
          Soft “one models page” merge stays deleted.
          Posture only.
        </p>
      </div>
      <div className="btn-row sa-models-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
          disabled=${cat.loading || posture.loading}>
          <${Ico} d="refresh" w=${14}/> Re-read
        </button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/settings/models")}>Settings · Model access</button>
        <button type="button" className="btn btn--go btn--sm"
          onClick=${() => go("/sa/ai-tools")}>Skills &amp; MCP</button>
      </div>
    </div>

    <${Card} title="SA models posture" meta="Live">
      ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
      ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
        say=${String(posture.error)}/>` : null}
      ${!posture.loading && !posture.error ? html`
        <div className="sa-models-desk__kpis" role="group" aria-label="SA models posture">
          ${[
            ["Seat", seat ? "open" : "—", seat ? "1" : "0", "status Live"],
            ["Product", product ? "flag on" : "—", product ? "1" : "0", "AI_PLATFORM"],
            ["Write", writeArmed ? "armed" : "—", writeArmed ? "1" : "0", "publish/grants"],
            ["BYOK path", "platform", "1", post.byok_path || "/ai/models/byok-policy/platform"],
          ].map(([k, v, on, em]) => html`
            <div key=${k} className="sa-models-desk__kpi" data-on=${on}>
              <span>${k}</span>
              <b>${v}</b>
              <em>${em}</em>
            </div>`)}
        </div>
        <p className="note">
          platform ${post.platform_path || "/ai/models/platform"}
          seats only
        </p>` : null}
    <//>

    <${Alert} tone="info" title="Platform catalogue is staff-only"
      say="Keys are write-only. End users never see base URLs or secrets — only labels they may spend. Publish counts only after the server accepts."/>

    ${cat.error ? html`
      <${Alert} tone="warn" title="Catalogue read partial"
        say=${String(cat.error)}/>` : null}

    <div className="sa-models-desk__kpis" role="group" aria-label="Platform model KPIs">
      ${[
        ["Published", kpis.published, kpis.published ? "1" : "0", "Published rows"],
        ["Hosted", kpis.hosted, kpis.hosted ? "1" : "0", "remote providers"],
        ["Local", kpis.local, kpis.local ? "1" : "0", "on hardware"],
        [
          "BYOK ceiling",
          kpis.byok === null ? "—" : (kpis.byok ? "on" : "off"),
          kpis.byok ? "1" : "0",
          byok.policy ? "Policy" : "waiting",
        ],
      ].map(([k, v, on, em]) => html`
        <div key=${k} className="sa-models-desk__kpi" data-on=${on}>
          <span>${k}</span>
          <b>${typeof v === "number" ? html`<${Ticker} value=${v}/>` : v}</b>
          <em>${em}</em>
        </div>`)}
    </div>

    <${Card} ai title="Platform models" meta=${kpis.published + " published"}
        sub="Staff catalogue. Grants name a username on this desk. Empty stays empty when the bus fails.">
      ${cat.loading ? html`<p className="sub">Reading…</p>` : null}
      <div className="mdl sa-models-desk__list">
        ${cat.models.map((m) => html`
          <div key=${m.model_id} className="mdl__r" data-kind=${kindOf(m)}
               data-state=${sel === m.model_id ? "ready" : (m.has_secret || m.provider === "ollama" ? "ready" : "key_missing")}>
            <span className="mdl__k">${kindOf(m) === "local" ? "on your hardware" : "hosted"}</span>
            <span className="mdl__t">
              <b>${m.label}</b>
              <code>${m.text_model || m.vision_model || m.provider}</code>
              <em>${m.is_default ? "platform default" : "available"}</em>
            </span>
            <button type="button" className="btn btn--ghost btn--sm"
              disabled=${!writeArmed}
              onClick=${() => setSel(m.model_id)}>
              ${sel === m.model_id ? "Selected" : "Grants"}</button>
            <button type="button" className="btn btn--ghost btn--sm" disabled=${writes.busy || !writeArmed}
                onClick=${async () => {
                  if (!writeArmed) {
                    toast && toast("Remove is closed on this seat.", "warn");
                    return;
                  }
                  const r = await writes.remove(m.model_id);
                  if (r) {
                    if (sel === m.model_id) setSel("");
                    cat.reload();
                    toast && toast("Removed · " + m.label, "warn");
                  } else toast && toast(writes.error || "Remove refused · nothing was changed", "bad");
                }}>Remove</button>
          </div>`)}
        ${!cat.loading && !cat.models.length ? html`
          <${Alert} tone="info" title="Nothing published yet empty"
            say="Publish a platform model below, then grant it to named users. Empty is empty — brochure model rows are not invented."/>` : null}
      </div>
    <//>

    ${sel && writeArmed ? html`
      <${Card} title=${"Grants · " + sel} meta="who may spend"
          sub="Revoking a grant takes effect on the next resolve — Soft second preference store invent stays deleted.">
        ${grants.loading ? html`<p className="sub">Reading grants…</p>` : null}
        ${grants.error ? html`
          <${Alert} tone="warn" title="Could not load grants" say=${String(grants.error)}/>` : null}
        <div className="rows">
          ${(grants.grants || []).map((g) => html`
            <div key=${g.grant_id || (g.grantee_kind + g.grantee_id)} className="row">
              <span className="main">
                <b>${g.grantee_kind}</b>
                <span>${g.grantee_id || "—"}</span>
              </span>
              <button type="button" className="btn btn--ghost btn--xs" disabled=${writes.busy}
                onClick=${async () => {
                  const r = await writes.revoke(sel, g.grant_id);
                  if (r) {
                    grants.reload();
                    toast && toast("Grant revoked", "warn");
                  } else toast && toast(writes.error || "Revoke refused · nothing was changed", "bad");
                }}>Revoke</button>
            </div>`)}
          ${!grants.loading && !(grants.grants || []).length
            ? html`<p className="note">No grants yet — nobody can spend this model.</p>` : null}
        </div>
        <div className="grid g2 sa-models-desk__grant" style=${{ gap: "0 16px", marginTop: 12 }}>
          <${Field} label="Grant to username">
            <${Input} icon="user" value=${grantUser} onInput=${(e) => setGrantUser(e.target.value)}
              placeholder="tenant-admin"/>
          <//>
          <div className="btn-row" style=${{ alignSelf: "end" }}>
            <button type="button" className="btn btn--go btn--sm"
              disabled=${writes.busy || !grantUser.trim()}
              onClick=${grant}>Grant user</button>
          </div>
        </div>
      <//>` : null}

    <${Card} title="Publish a platform model"
        sub="Keys are write-only. Success only after the server confirms.">
      ${!writeArmed ? html`<p className="note">Publish form off until the write seat is open.</p>` : html`
      <div className="grid g2" style=${{ gap: "0 16px" }}>
        <${Field} label="Label">
          <${Input} icon="brain" value=${draft.label}
            onInput=${(e) => setDraft((d) => ({ ...d, label: e.target.value }))} placeholder="Staff Claude"/>
        <//>
        <${Field} label="Provider">
          <${Input} icon="chip" value=${draft.provider}
            onInput=${(e) => setDraft((d) => ({ ...d, provider: e.target.value }))} placeholder="openai_compat"/>
        <//>
        <${Field} label="Base URL" hint="https for hosted. http only for a private network host.">
          <${Input} icon="link" value=${draft.base_url}
            onInput=${(e) => setDraft((d) => ({ ...d, base_url: e.target.value }))}
            placeholder="https://api.example.invalid/v1"/>
        <//>
        <${Field} label="Model name">
          <${Input} icon="chip" value=${draft.text_model}
            onInput=${(e) => setDraft((d) => ({ ...d, text_model: e.target.value }))} placeholder="claude-sonnet-4"/>
        <//>
        <${Field} label="API key" hint="Write-only. Never logged, never in a response.">
          <${Input} icon="lock" type="password" value=${draft.api_key}
            onInput=${(e) => setDraft((d) => ({ ...d, api_key: e.target.value }))} placeholder="••••••••"/>
        <//>
      </div>
      <div className="btn-row" style=${{ marginTop: 12 }}>
        <button type="button" className="btn btn--go btn--sm" disabled=${writes.busy} onClick=${publish}>
          ${writes.busy ? "Publishing…" : "Publish"}</button>
        <button type="button" className="btn btn--ghost btn--sm" disabled=${writes.busy} onClick=${flipByok}>
          ${byok.policy && byok.policy.allow_byok ? "Lower BYOK ceiling" : "Raise BYOK ceiling"}</button>
      </div>
      ${byok.policy ? html`<p className="note">Platform BYOK ceiling: ${byok.policy.allow_byok ? "on" : "off"}.
        Tenant admins cannot raise above this ceiling.</p>` : html`
        <p className="note">BYOK policy waiting on Live.</p>`}
      ${writes.error ? html`<${Alert} tone="bad" title="Write refused" say=${writes.error}/>` : null}`}
    <//>
  </div>`;
}

export function PlatformModels({ toast }) {
  return html`<${F} data-cmc-page="page122-sa-models-mount">
    <${SaModelsDesk} toast=${toast}/>
  <//>`;
}
